import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ConnectionStatus} from 'instruments/connections/ConnectionConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {capitalize} from 'util/NameUtil';

export const CONNECTION_STATUS_TO_STYLE: {[status in ConnectionStatus]: keyof typeof styles} = {
  [ConnectionStatus.AVAILABLE]: 'available',
  [ConnectionStatus.INSTRUMENT_ERROR]: 'warning',
  [ConnectionStatus.COMMUNICATION_ERROR]: 'warning',
  [ConnectionStatus.DISABLED]: 'pending',
  [ConnectionStatus.PENDING]: 'pending',
  [ConnectionStatus.RUNNING]: 'running',
  [ConnectionStatus.SERVICE_UNAVAILABLE]: 'warning',
};

export const CONNECTION_STATUS_TO_TEXT: {[status in ConnectionStatus]: string} = {
  [ConnectionStatus.AVAILABLE]: 'Available',
  [ConnectionStatus.INSTRUMENT_ERROR]: 'Instrument error',
  [ConnectionStatus.COMMUNICATION_ERROR]: 'Unreachable',
  [ConnectionStatus.DISABLED]: 'Disabled',
  [ConnectionStatus.PENDING]: 'Pending',
  [ConnectionStatus.RUNNING]: 'Running',
  [ConnectionStatus.SERVICE_UNAVAILABLE]: 'Instrument service unavailable',
};

export const CONNECTION_STATUS_TO_EQUIPMENT_TEXT: {[status in ConnectionStatus]: string} = {
  [ConnectionStatus.AVAILABLE]: 'Available',
  [ConnectionStatus.INSTRUMENT_ERROR]: 'Instrument error',
  [ConnectionStatus.COMMUNICATION_ERROR]: "Equipment's connection unreachable",
  [ConnectionStatus.DISABLED]: "Equipment's connection disabled",
  [ConnectionStatus.PENDING]: "Equipment's connection pending",
  [ConnectionStatus.RUNNING]: "Equipment's connection running",
  [ConnectionStatus.SERVICE_UNAVAILABLE]: 'Instrument service unavailable',
};

const CONNECTION_STATUS_TO_TOOLTIP: {[status in ConnectionStatus]: string} = {
  [ConnectionStatus.AVAILABLE]: '',
  [ConnectionStatus.INSTRUMENT_ERROR]: 'Please check instrument status',
  [ConnectionStatus.COMMUNICATION_ERROR]:
    'Benchling is not receiving messages for this connection. If the gateway is open and you are still experiencing issues, please contact Benchling Support.',
  [ConnectionStatus.DISABLED]: '',
  [ConnectionStatus.PENDING]:
    'Pending communication from gateway - please install gateway if not already installed',
  [ConnectionStatus.RUNNING]: '',
  [ConnectionStatus.SERVICE_UNAVAILABLE]: 'Please contact Benchling support',
};

export const CONNECTION_STATUS_TO_ICON: {[status in ConnectionStatus]: BenchlingIconName} = {
  [ConnectionStatus.AVAILABLE]: 'status-success',
  [ConnectionStatus.INSTRUMENT_ERROR]: 'status-warning',
  [ConnectionStatus.COMMUNICATION_ERROR]: 'status-warning',
  [ConnectionStatus.DISABLED]: 'lock',
  [ConnectionStatus.PENDING]: 'request-pending',
  [ConnectionStatus.RUNNING]: 'request-in-progress',
  [ConnectionStatus.SERVICE_UNAVAILABLE]: 'status-warning',
};

export default function ConnectionStatusDisplay(props: {status: ConnectionStatus}): JSX.Element {
  return (
    <>
      <Tooltip
        title={CONNECTION_STATUS_TO_TOOLTIP[props.status]}
        container="body"
        delay={{show: 500, hide: 100}}
      >
        <BenchlingIcon
          className={css(styles.statusIcon, styles[CONNECTION_STATUS_TO_STYLE[props.status]])}
          icon={CONNECTION_STATUS_TO_ICON[props.status]}
        />
        {capitalize(CONNECTION_STATUS_TO_TEXT[props.status])}
      </Tooltip>
    </>
  );
}

export const styles = StyleSheet.create({
  statusIcon: {
    marginRight: '6px',
  },
  available: {
    color: Colors.GREEN,
  },
  pending: {
    color: Colors.GRAY_DARK,
  },
  running: {
    color: Colors.BLUE,
  },
  warning: {
    color: Colors.ORANGE,
  },
});
